/**
 * Billing configuration: available claim forms, claim status display,
 * and who can do what. Add a new claim form by adding one entry to CLAIM_FORMS.
 */
import { SvgIconComponent } from "@mui/icons-material";
import {
  LocalHospital as ProfessionalIcon,
  Apartment as InstitutionalIcon,
  ReceiptLong as InvoiceIcon,
} from "@mui/icons-material";
import { ChipProps } from "@mui/material";
import { ClaimFormType, ClaimStatus, InvoiceLine } from "../../types/billing.types";

export interface ClaimFormDefinition {
  id: ClaimFormType;
  name: string;
  altName: string;
  category: "Professional" | "Institutional" | "Individual" | "Invoice" | "Private Pay";
  description: string;
  icon: SvgIconComponent;
}

export const ALL_CLAIM_FORMS: ClaimFormDefinition[] = [
  {
    id: ClaimFormType.CMS_1500,
    name: "CMS-1500",
    altName: "HCFA-1500",
    category: "Professional",
    description: "Physicians, clinical visits, therapies and other professional care services.",
    icon: ProfessionalIcon,
  },
  {
    id: ClaimFormType.UB_04,
    name: "UB-04",
    altName: "CMS-1450",
    category: "Institutional",
    description: "Skilled nursing facilities, inpatient hospice and other institutional care.",
    icon: InstitutionalIcon,
  },
  {
    id: ClaimFormType.INVOICE,
    name: "Individual Invoice",
    altName: "Caregiving & Wrap Around Services",
    category: "Individual",
    description: "Assisted living caregiving, wrap around services, and resident private billing.",
    icon: InvoiceIcon,
  },
];

// Active claim forms for Insurance Claims (CMS-1500 and UB-04)
export const CLAIM_FORMS: ClaimFormDefinition[] = ALL_CLAIM_FORMS.filter(
  (form) => form.id !== ClaimFormType.INVOICE,
);

export const getClaimForm = (id?: string): ClaimFormDefinition | undefined => {
  if (!id) return undefined;
  const normalized = id.toUpperCase().replace("-", "_");
  return ALL_CLAIM_FORMS.find((f) => f.id === normalized || f.id.toUpperCase() === normalized);
};

export const CLAIM_STATUS_META: Record<ClaimStatus, { label: string; color: ChipProps["color"] }> = {
  [ClaimStatus.DRAFT]: { label: "Draft", color: "default" },
  [ClaimStatus.SUBMITTED]: { label: "Submitted", color: "info" },
  [ClaimStatus.IN_REVIEW]: { label: "In Review", color: "warning" },
  [ClaimStatus.APPROVED]: { label: "Approved", color: "success" },
  [ClaimStatus.DENIED]: { label: "Rejected", color: "error" },
  [ClaimStatus.PAID]: { label: "Paid", color: "success" },
  [ClaimStatus.PARTIAL_PAID]: { label: "Partially Paid", color: "warning" },
};

// Roles with access to billing module (only Super admin, facility admin and admin)
export const BILLING_ROLES = ["SUPER_ADMIN", "ADMIN", "FACILITY_ADMIN"];
export const BILLING_APPROVER_ROLES = ["SUPER_ADMIN", "ADMIN", "FACILITY_ADMIN"];

export const canAccessBilling = (role?: string) => {
  if (!role) return false;
  const normalized = role.toUpperCase().replace(/^ROLE_/, "").trim();
  if (normalized === "CAREGIVER" || normalized === "LEAD_CAREGIVER" || normalized === "NURSE") {
    return false;
  }
  return BILLING_ROLES.includes(normalized);
};

export const canApproveClaims = (role?: string) => {
  if (!role) return false;
  const normalized = role.toUpperCase().replace(/^ROLE_/, "").trim();
  return BILLING_APPROVER_ROLES.includes(normalized);
};

/** Mirror SettlementPermissions in the API: who may post payments, and who may void/refund/adjust. */
export const SETTLEMENT_ROLES = ["SUPER_ADMIN", "ADMIN", "FACILITY_ADMIN"];
export const SETTLEMENT_CORRECTION_ROLES = ["SUPER_ADMIN", "ADMIN"];

export const canPostSettlements = (role?: string) => !!role && SETTLEMENT_ROLES.includes(role);
export const canCorrectSettlements = (role?: string) => !!role && SETTLEMENT_CORRECTION_ROLES.includes(role);

/** User-facing message from an API error, including field validation messages. */
export const getApiErrorMessage = (err: any, fallback: string): string => {
  const data = err?.response?.data;
  const fieldMessages = Object.values((data?.validationErrors as Record<string, string[]>) || {}).flat();
  if (fieldMessages.length > 0) return fieldMessages.join("; ");
  return data?.message || err?.message || fallback;
};

/**
 * Normalizes previous services API response into InvoiceLine array
 */
export const normalizePreviousServices = (raw: any): InvoiceLine[] => {
  if (!raw) return [];

  let list: any[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (raw && typeof raw === "object") {
    if (Array.isArray(raw.data)) list = raw.data;
    else if (Array.isArray(raw.data?.services)) list = raw.data.services;
    else if (Array.isArray(raw.data?.items)) list = raw.data.items;
    else if (Array.isArray(raw.data?.previousServices)) list = raw.data.previousServices;
    else if (Array.isArray(raw.data?.content)) list = raw.data.content;
    else if (Array.isArray(raw.services)) list = raw.services;
    else if (Array.isArray(raw.items)) list = raw.items;
    else if (Array.isArray(raw.previousServices)) list = raw.previousServices;
    else if (Array.isArray(raw.content)) list = raw.content;
    else if (raw.productOrService || raw.serviceName || raw.service) {
      list = [raw];
    } else if (raw.data && typeof raw.data === "object" && (raw.data.productOrService || raw.data.serviceName || raw.data.service)) {
      list = [raw.data];
    }
  }

  // If array has invoice objects with items array, flatten all items
  if (list.length > 0 && list.some((item) => Array.isArray(item?.items))) {
    list = list.flatMap((inv) => (Array.isArray(inv?.items) ? inv.items : [inv]));
  }

  return list
    .filter((item) => item && typeof item === "object")
    .map((item, idx) => {
      const productOrService =
        item.productOrService ||
        item.serviceName ||
        item.service ||
        item.name ||
        item.title ||
        "Caregiving";

      const description =
        item.description ||
        item.serviceDescription ||
        item.notes ||
        "";

      const qty = Math.max(1, Number(item.quantity ?? item.qty ?? 1) || 1);
      const rate = Math.max(0, Number(item.rate ?? item.unitRate ?? item.price ?? item.unitPrice ?? 0) || 0);
      const amount = Math.round((Number(item.amount ?? item.total ?? item.totalAmount ?? (qty * rate)) || (qty * rate)) * 100) / 100;

      const serviceLookupId =
        item.serviceLookupId ||
        item.serviceId ||
        item.lookupId ||
        item.id ||
        undefined;

      const uniqueLineId = String(
        item.id ? `line-${item.id}-${idx}` : `line-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}`
      );

      return {
        id: uniqueLineId,
        productOrService: String(productOrService).trim(),
        description: String(description).trim(),
        qty,
        rate,
        amount,
        serviceLookupId,
      };
    });
};

